import { useState, useReducer, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
// TODO: Add react-helmet-async when React 19 support is available for SEO meta tags
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { organizationApi } from "../../api/organization";
import { ticketApi } from "../../api/ticket";

import EventDetailsForm from "../../components/ticketing/EventDetailsForm";
import TicketSetupForm from "../../components/ticketing/TicketSetupForm";
import CheckoutForm from "../../components/ticketing/CheckoutForm";
import WizardSteps from "../../components/ticketing/WizardSteps";
import AppHeader from "../../components/AppHeader";
import ConfirmationModal from "../../components/ConfirmationModal";
import CustomFormInput from "../../components/forms/CustomFormInput";
import BaseModal from "../../components/BaseModal";
import Icon from "@mdi/react";
import { mdiCheckCircle, mdiAlertCircleOutline, mdiDownload, mdiPrinterOutline, mdiArrowULeftTop } from "@mdi/js";

import "../header.css";
import "../Common.css"; /* Common styles for all pages */
import "./TicketWizard.css";
import { useDevMode } from "../../hooks/useDevMode";
import usePayoutsReady from "../../hooks/usePayoutsReady";
import { isoToLocalDateTimeInput, localInputToUTCISO } from "../../utils/datetimeLocal";

// Money normalization (tickets cents PRP): the wizard keeps ticket prices in DOLLARS
// (the organiser types dollars in the input), while the API stores integer CENTS — the
// same convention as the instructor hourly-rate input. Convert only at the persistence
// boundary: ×100 when saving a draft, ÷100 when loading one back into the form.
const ticketTypesToCents = (types) =>
    Array.isArray(types) ? types.map(t => ({ ...t, price: Math.round((Number(t.price) || 0) * 100) })) : types;
const ticketTypesToDollars = (types) =>
    Array.isArray(types) ? types.map(t => ({ ...t, price: (Number(t.price) || 0) / 100 })) : types;

/**
 * MIGRATION NOTE (2026-02-06): Migrated ownership acknowledgment checkbox to CustomFormInput
 * Part of DL-04FEB26-01: CustomFormInput migration
 */

// Steps configuration for easy management
const WIZARD_STEPS = [
    { id: 1, title: "Event Details", component: "EventDetailsForm" },
    { id: 2, title: "Ticket Setup", component: "TicketSetupForm" },
    { id: 3, title: "Checkout", component: "CheckoutForm" },
];

// Initial state for form data
const initialFormData = {
    eventDetails: {
        title: "",
        description: "",
        startDateTime: "",
        endDateTime: "",
        timezone: "", // IANA event display tz; seeded to the org's business-location tz for new events
        location: {
            description: "",
            latitude: null,
            longitude: null,
        },
        importSource: "manual",
        icsData: null,
    },
    ticketSetup: {
        ticketTypes: [],
        salesPeriod: {
            startDate: "",
            endDate: "",
        },
        refundPolicy:
            "Refunds are allowed up to 24 hours before the event. Processing fees are non-refundable. Contact organizer for special circumstances.",
    },
    checkout: {
        paymentMethods: ['stripe'], // MVP: Stripe only. Multiple payment methods is future roadmap
        termsAndConditions:
            "By purchasing tickets to this event, you agree to abide by all venue rules and event policies. Tickets are non-transferable unless otherwise stated. The organizer reserves the right to refuse entry or remove attendees who violate event policies. Please arrive on time as late entry may not be permitted during performances.",
    },
    currentStep: 1,
    eventId: null, // The event ID in the event-tickets collection (for editing existing event drafts)
};

// Reducer for form state management
function formReducer(state, action) {
    switch (action.type) {
        case "UPDATE_SECTION":
            return {
                ...state,
                [action.section]: {
                    ...state[action.section],
                    ...action.data,
                },
            };
        case "SET_STEP":
            return {
                ...state,
                currentStep: action.step,
            };
        case "SET_EVENT_ID":
            return {
                ...state,
                eventId: action.eventId,
            };
        case "LOAD_DRAFT":
            return {
                ...state,
                ...action.data,
            };
        case "RESET_FORM":
            return initialFormData;
        default:
            return state;
    }
}

const TicketWizard = () => {
    const { user, loading, isAuthenticated } = useAuth();
    const { isDevMode } = useDevMode();
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, dispatch] = useReducer(formReducer, initialFormData);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [, setDefaultsLoaded] = useState(false);
    const [showQRCode, setShowQRCode] = useState(false);
    const [qrCodeData, setQRCodeData] = useState(null);
    const [, setPublishedEvent] = useState(null);
    const [showOwnershipModal, setShowOwnershipModal] = useState(false);
    const [ownershipPrompt, setOwnershipPrompt] = useState(null);
    const [ownershipAcknowledged, setOwnershipAcknowledged] = useState(false);

    // Edit existing tickets confirmation modal state
    const [showEditTicketsModal, setShowEditTicketsModal] = useState(false);
    const [existingEventData, setExistingEventData] = useState(null);

    // Per-ticket-type sold counts (published events only) — lets the setup form floor
    // each quantity input at what's already sold. Keyed by ticket-type name.
    const [soldCounts, setSoldCounts] = useState(null);

    // Draft-vs-published edit separation: true when editing a published event that has a
    // parked (unpublished) edit. Drives the "Published — unpublished changes" indicator +
    // the Discard action. Derived from the backend `hasUnpublishedChanges` response flag.
    const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
    // Which sections differ from the live version, e.g. [{ section, label }]. Powers the
    // "what changed" summary next to the Discard action so the organiser can see, before
    // publishing or discarding, exactly what they edited. Backend-derived (real diff).
    const [unpublishedChanges, setUnpublishedChanges] = useState([]);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    const [isDiscarding, setIsDiscarding] = useState(false);

    // Org base currency — ticket prices are charged in it (org.currency is the single
    // source of truth; the backend stamps it onto every ticket type on save). The wizard
    // shows it read-only so the organiser knows which currency their prices are in.
    const [orgCurrency, setOrgCurrency] = useState('AUD');
    // The org's IANA timezone (derived backend-side from its business location). Event times
    // are entered, stored, and displayed in this zone. Null until loaded / if no org location set.
    const [orgTimezone, setOrgTimezone] = useState(null);

    // Stripe Connect readiness — publishing is blocked when the org positively has
    // no active payout account (buyers pay by card, so a published event must be
    // chargeable). null = unknown (loading/fetch failed) and does not block; the
    // backend publish endpoint enforces the same gate regardless.
    const payoutsReady = usePayoutsReady(user?.organisationId, isAuthenticated);

    // Track if initial load has completed to prevent infinite loops
    const hasLoadedDraft = useRef(false);
    const hasLoadedDefaults = useRef(false);

    // Detect edit mode from URL parameters
    const searchParams = new URLSearchParams(location.search);
    const editMode = searchParams.get('mode') === 'edit';



    useEffect(() => {
        if (!loading && !isAuthenticated) {
            navigate("/login");
        }
    }, [loading, isAuthenticated, navigate]);

    // Load the org's base currency + timezone. Currency drives ticket pricing display;
    // timezone drives how event times are interpreted on submit and rendered everywhere.
    useEffect(() => {
        if (!user?.organisationId) return;
        organizationApi.getOrgProfile(user.organisationId)
            .then(org => {
                setOrgCurrency(org.currency || 'AUD');
                setOrgTimezone(org.timezone || null);
            })
            .catch(() => {});
    }, [user?.organisationId]);

    // Seed a NEW event's timezone with the org's business-location tz (AC1 default). Runs once
    // orgTimezone resolves; guarded so it never clobbers a loaded draft's stored tz or a value
    // the organiser already picked — only fills an empty field on an unsaved new event.
    useEffect(() => {
        if (!orgTimezone || editMode) return;
        if (formData.eventId) return; // existing draft — its stored tz is authoritative
        if (formData.eventDetails.timezone) return; // already seeded or user-picked
        dispatch({ type: "UPDATE_SECTION", section: "eventDetails", data: { timezone: orgTimezone } });
    }, [orgTimezone, editMode, formData.eventId, formData.eventDetails.timezone]);

    useEffect(() => {
        // Check if user is part of organisation (dance school)
        if (!loading && isAuthenticated && !user.organisationId) {
            navigate("/uploads"); // Redirect to individual user page
        }
    }, [loading, isAuthenticated, user.organisationId, navigate]);

    // Load organisation defaults for new events
    const loadOrganisationDefaults = useCallback(async () => {
        try {
            console.log("Loading organisation defaults for new event...");
            let settings;
            try {
                settings = await organizationApi.getSettings(user.organisationId);
            } catch (err) {
                console.log("No organisation settings found, loading system defaults");
                await loadSystemTicketTemplate();
                setDefaultsLoaded(true);
                return;
            }

            if (settings?.ticketingDefaults) {
                const allDefaultTicketTypes = settings.ticketingDefaults.defaultTicketTypes || [];
                const primaryDefaultIndex = settings.ticketingDefaults.primaryDefaultIndex || 0;

                // Use only the primary default ticket type
                const primaryTicketType = allDefaultTicketTypes[primaryDefaultIndex];
                const defaultTicketTypes = primaryTicketType ? [primaryTicketType] : [];

                // Apply organisation defaults to form
                const dispatchData = {
                    ticketTypes: defaultTicketTypes,
                    refundPolicy:
                        settings.ticketingDefaults.defaultRefundPolicy || initialFormData.ticketSetup.refundPolicy,
                };

                dispatch({
                    type: "UPDATE_SECTION",
                    section: "ticketSetup",
                    data: dispatchData,
                });

                dispatch({
                    type: "UPDATE_SECTION",
                    section: "checkout",
                    data: {
                        termsAndConditions:
                            settings.ticketingDefaults.defaultTermsAndConditions ||
                            initialFormData.checkout.termsAndConditions,
                    },
                });
            } else {
                await loadSystemTicketTemplate();
            }
            setDefaultsLoaded(true);
        } catch (error) {
            console.error("Error loading organisation defaults:", error);
            // Continue with system defaults if loading fails
            setDefaultsLoaded(true);
        }
    }, [user.organisationId, dispatch]);

    // Load system ticket template as fallback when organization has no defaults
    const loadSystemTicketTemplate = async () => {
        try {
            const templates = await organizationApi.getTicketTemplates();

            // Find the system default template
            const defaultTemplate = templates.find(t => t.isSystemDefault) ||
                                    templates.find(t => t.templateId === 'simple-dance-event') ||
                                    templates[0];

            if (defaultTemplate) {
                // Apply system template defaults to form
                dispatch({
                    type: "UPDATE_SECTION",
                    section: "ticketSetup",
                    data: {
                        ticketTypes: defaultTemplate.ticketTypes || [],
                        refundPolicy: defaultTemplate.refundPolicy || initialFormData.ticketSetup.refundPolicy,
                    },
                });

                dispatch({
                    type: "UPDATE_SECTION",
                    section: "checkout",
                    data: {
                        termsAndConditions: defaultTemplate.termsAndConditions || initialFormData.checkout.termsAndConditions,
                    },
                });
            }
        } catch (error) {
            console.error("Error loading system ticket template:", error);
        }
    };

    const loadExistingDraft = useCallback(async draftId => {
        // Prevent multiple loads of the same draft
        if (hasLoadedDraft.current) {
            return;
        }

        try {
            hasLoadedDraft.current = true;
            setIsLoading(true);

            const draftData = await ticketApi.getDraft(draftId);

            // Surface whether this published event has a parked (unpublished) edit. The
            // editable surface getDraft returns already reflects the parked values.
            setHasUnpublishedChanges(!!draftData?.hasUnpublishedChanges);
            setUnpublishedChanges(draftData?.unpublishedChanges || []);

            // For a published event, load per-type sold counts so the setup form can
            // floor each quantity at what's already sold. Best-effort — never blocks load.
            if (draftData?.status === "published") {
                try {
                    const soldTypes = await ticketApi.getSoldMetadata(draftId);
                    const byName = {};
                    soldTypes.forEach(t => { byName[t.name] = t.sold; });
                    setSoldCounts(byName);
                } catch (soldErr) {
                    console.error("Failed to load sold counts:", soldErr);
                }
            }

            if (draftData) {

                // Backend stores event times as UTC ISO. Convert them to the org's local
                // wall-clock so the date/time pickers show the correct entered time (org tz
                // when the org has a location, else browser-local). Resolve tz best-effort.
                let loadTz = orgTimezone;
                if (!loadTz && user?.organisationId) {
                    try {
                        const org = await organizationApi.getOrgProfile(user.organisationId);
                        loadTz = org?.timezone || null;
                    } catch { /* fall back to browser-local */ }
                }
                // The event's OWN stored timezone wins (the wizard shows every wall-clock time in
                // the event's zone); org tz is only the fallback for a legacy draft with no stored tz.
                const eventTz = draftData.eventDetails?.timezone || loadTz;
                if (draftData.eventDetails) {
                    draftData.eventDetails = {
                        ...draftData.eventDetails,
                        startDateTime: isoToLocalDateTimeInput(draftData.eventDetails.startDateTime, eventTz),
                        endDateTime: isoToLocalDateTimeInput(draftData.eventDetails.endDateTime, eventTz),
                    };
                }
                if (draftData.ticketSetup?.salesPeriod) {
                    const sp = draftData.ticketSetup.salesPeriod;
                    draftData.ticketSetup = {
                        ...draftData.ticketSetup,
                        salesPeriod: {
                            ...sp,
                            startDate: sp.startDate ? isoToLocalDateTimeInput(sp.startDate, eventTz) : sp.startDate,
                            endDate: sp.endDate ? isoToLocalDateTimeInput(sp.endDate, eventTz) : sp.endDate,
                        },
                    };
                }

                // Saved drafts store integer cents; the form edits dollars — convert back.
                if (Array.isArray(draftData.ticketSetup?.ticketTypes)) {
                    draftData.ticketSetup = {
                        ...draftData.ticketSetup,
                        ticketTypes: ticketTypesToDollars(draftData.ticketSetup.ticketTypes),
                    };
                }

                // Apply defaults if certain fields are empty
                if (!draftData.ticketSetup?.refundPolicy) {
                    draftData.ticketSetup = {
                        ...draftData.ticketSetup,
                        refundPolicy:
                            "Refunds are allowed up to 24 hours before the event. Processing fees are non-refundable. Contact organizer for special circumstances.",
                    };
                }

                // Apply organisation default ticket types if draft has empty ticket types
                if (!draftData.ticketSetup?.ticketTypes || draftData.ticketSetup.ticketTypes.length === 0) {
                    // Re-fetch organization defaults for draft
                    try {
                        const settings = await organizationApi.getSettings(user.organisationId);
                        if (settings?.ticketingDefaults?.defaultTicketTypes) {
                            const allDefaults = settings.ticketingDefaults.defaultTicketTypes;
                            const primaryIndex = settings.ticketingDefaults.primaryDefaultIndex || 0;
                            const primaryTicketType = allDefaults[primaryIndex];
                            const defaultTicketTypes = primaryTicketType ? [primaryTicketType] : [];

                            if (defaultTicketTypes.length > 0) {
                                draftData.ticketSetup = {
                                    ...draftData.ticketSetup,
                                    ticketTypes: defaultTicketTypes,
                                };
                            }
                        }
                    } catch (error) {
                        console.error("Error fetching defaults for draft:", error);
                    }
                }

                if (!draftData.checkout?.termsAndConditions) {
                    draftData.checkout = {
                        ...draftData.checkout,
                        termsAndConditions:
                            "By purchasing tickets to this event, you agree to abide by all venue rules and event policies. Tickets are non-transferable unless otherwise stated. The organizer reserves the right to refuse entry or remove attendees who violate event policies. Please arrive on time as late entry may not be permitted during performances.",
                    };
                }

                dispatch({
                    type: "LOAD_DRAFT",
                    data: {
                        ...draftData,
                        eventId: draftId,
                        // A FRESH edit of a published event (no parked changes yet) starts at
                        // Step 1. A RESUMED parked edit, or a never-published draft, opens on
                        // the last step it was saved on. The user can click back to any step.
                        currentStep: (editMode && !draftData.hasUnpublishedChanges) ? 1 : (draftData.currentStep || 1),
                    },
                });
                toast.success("Draft loaded successfully");
            }
        } catch (error) {
            console.error("Error loading draft:", error);
            toast.error("Failed to load draft. Starting with new ticket.");
            hasLoadedDraft.current = false; // Allow retry on error
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, user.organisationId]); // REMOVED formData.ticketSetup dependency

    // Load organisation defaults and existing draft (runs ONCE on mount)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const draftId = urlParams.get("draft");

        // Check if we've already loaded
        if (hasLoadedDefaults.current) {
            return;
        }

        // Load organisation defaults first (if we have an orgId and haven't loaded yet)
        if (user?.organisationId && !hasLoadedDefaults.current) {
            hasLoadedDefaults.current = true;

            loadOrganisationDefaults().then(() => {
                setDefaultsLoaded(true);
                // After defaults are loaded, load draft if there's a draft ID
                if (draftId) {
                    loadExistingDraft(draftId);
                }
            });
        }
        // Only run once on mount or when orgId becomes available
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.organisationId]);

    // Dev mode is now controlled globally from OrgAdminConsole
    // This component just displays the current state (read-only)
    // Removed handleDevModeToggle function since toggle moved to dashboard header

    const updateFormSection = (section, data) => {
        dispatch({
            type: "UPDATE_SECTION",
            section,
            data,
        });
        setError(""); // Clear any previous errors

        // Show success message when ICS file is imported
        if (section === "eventDetails" && data.importSource === "ics" && data.title) {
            toast.success(`Successfully imported: ${data.title}`);
        }
    };

    const goToStep = step => {
        dispatch({
            type: "SET_STEP",
            step,
        });

        // Scroll to top of page for better UX when changing steps
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const validateCurrentStep = () => {
        switch (formData.currentStep) {
            case 2: // Ticket Setup validation
                const ticketTypes = formData.ticketSetup?.ticketTypes || [];
                if (ticketTypes.length === 0) {
                    setError("Please add at least one ticket type before proceeding.");
                    return false;
                }

                // Validate each ticket type
                const invalidTickets = [];
                ticketTypes.forEach((ticket, index) => {
                    const missingFields = [];
                    if (!ticket.name?.trim()) missingFields.push("name");
                    if (ticket.price === undefined || ticket.price === null || ticket.price === "" || isNaN(Number(ticket.price)) || Number(ticket.price) < 0)
                        missingFields.push("price");
                    if (!ticket.quantity || ticket.quantity <= 0) missingFields.push("quantity");

                    if (missingFields.length > 0) {
                        invalidTickets.push(`Ticket ${index + 1}: ${missingFields.join(", ")}`);
                    }
                });

                if (invalidTickets.length > 0) {
                    setError(`Please complete required fields: ${invalidTickets.join("; ")}`);
                    return false;
                }
                break;

            default:
                break;
        }
        return true;
    };

    const goToNextStep = async () => {
        try {
            setIsLoading(true);
            setError("");

            // Validate current step before proceeding
            if (!validateCurrentStep()) {
                setIsLoading(false);
                return;
            }

            // Save current form data to backend
            const success = await saveDraft();

            if (success && formData.currentStep < WIZARD_STEPS.length) {
                goToStep(formData.currentStep + 1);
                toast.success("Progress saved successfully");
            }
        } catch (error) {
            console.error("Error advancing to next step:", error);
            toast.error("Failed to save progress. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const goToPreviousStep = () => {
        if (formData.currentStep > 1) {
            goToStep(formData.currentStep - 1);
        }
    };

    const saveDraft = async () => {
        try {
            // Convert the entered wall-clock times to UTC, interpreting them in the EVENT's own
            // timezone (the zone shown in the wizard's timezone field). Falls back to the org tz,
            // then browser-local. `eventDetails.timezone` rides along in the spread → persisted.
            const eventDetails = { ...formData.eventDetails };
            const saveTz = eventDetails.timezone || orgTimezone;
            eventDetails.startDateTime = localInputToUTCISO(eventDetails.startDateTime, saveTz);
            eventDetails.endDateTime = localInputToUTCISO(eventDetails.endDateTime, saveTz);

            // Remove icsData if it's null (backend expects object or omitted, not null)
            if (eventDetails.icsData === null) {
                delete eventDetails.icsData;
            }

            // Clean up the payload - remove empty date strings and set default refund policy
            const ticketSetup = { ...formData.ticketSetup };
            if (ticketSetup.salesPeriod) {
                ticketSetup.salesPeriod = { ...ticketSetup.salesPeriod };
                // Remove empty date strings; convert non-empty ones to UTC in the event's tz.
                if (!ticketSetup.salesPeriod.startDate) {
                    delete ticketSetup.salesPeriod.startDate;
                } else {
                    ticketSetup.salesPeriod.startDate = localInputToUTCISO(ticketSetup.salesPeriod.startDate, saveTz);
                }
                if (!ticketSetup.salesPeriod.endDate) {
                    delete ticketSetup.salesPeriod.endDate;
                } else {
                    ticketSetup.salesPeriod.endDate = localInputToUTCISO(ticketSetup.salesPeriod.endDate, saveTz);
                }
                // If both dates are empty, remove salesPeriod entirely
                if (Object.keys(ticketSetup.salesPeriod).length === 0) {
                    delete ticketSetup.salesPeriod;
                }
            }

            // Set default refund policy if empty
            if (!ticketSetup.refundPolicy || ticketSetup.refundPolicy.trim() === "") {
                ticketSetup.refundPolicy =
                    "Refunds are allowed up to 24 hours before the event. Processing fees are non-refundable. Contact organizer for special circumstances.";
            }

            // Persist ticket-type prices as integer cents (the form holds dollars).
            ticketSetup.ticketTypes = ticketTypesToCents(ticketSetup.ticketTypes);

            const payload = {
                eventDetails,
                ticketSetup,
                checkout: {
                    // Send ONLY the schema-allowed checkout fields. Spreading
                    // ...formData.checkout leaks server-managed fields (e.g. `published`)
                    // back to POST /draft, which the draft schema rejects — this broke
                    // editing an already-published event.
                    paymentMethods: ['stripe'], // MVP: Stripe only, preset for backend validation
                    termsAndConditions: formData.checkout?.termsAndConditions ?? '',
                },
                currentStep: formData.currentStep,
            };

            // Only include eventId if it exists (for updates)
            if (formData.eventId) {
                payload.eventId = formData.eventId;
            }

            const response = await ticketApi.saveDraft(payload);

            // REMOVED: alreadyOwned handling - now done during ICS import phase
            // Ownership validation happens in /api/v2/event/check-duplicates
            // This eliminates redundant checks and improves performance

            // Check for ownership acknowledgment requirement (still used for manual entry)
            if (response.data?.requiresOwnershipAck) {
                setOwnershipPrompt(response.data.ownershipPrompt);
                setShowOwnershipModal(true);
                setIsLoading(false);
                return false; // Don't proceed with normal flow
            }

            if (response.ok) {
                // Update form with returned event ID if it's a new draft
                if (!formData.eventId && response.data.ticketDraft.id) {
                    // Update eventId directly in state
                    dispatch({
                        type: "SET_EVENT_ID",
                        eventId: response.data.ticketDraft.id,
                    });
                    // Update URL to include draft ID
                    window.history.replaceState({}, "", `?draft=${response.data.ticketDraft.id}`);
                }
                // Saving an edit to a published event parks it as a draft edit — reflect the
                // real diff immediately so the Discard action + "what changed" summary appear
                // (or clear, if the save left the config identical to live) without a reload.
                if (response.data?.ticketDraft) {
                    setHasUnpublishedChanges(!!response.data.ticketDraft.hasUnpublishedChanges);
                    setUnpublishedChanges(response.data.ticketDraft.unpublishedChanges || []);
                }
                return true;
            } else {
                // Handle API error response (webApiClient returns error object with ok: false)
                console.error("API error response:", response);
                console.error("Response data contents:", JSON.stringify(response.data, null, 2));

                // V2 API error format: { error: { code, message, details } }
                let errorMessage = "Failed to save draft. Please try again.";

                // With flattened error format from interceptor, just access message directly
                if (response.data?.code === "VALIDATION_ERROR") {
                    // Show the actual validation error message
                    errorMessage = response.data.message || "Validation failed";

                    // Add helpful context if available
                    if (response.data.details?.field) {
                        const fieldName = response.data.details.field
                            .replace(/eventDetails\./g, "")
                            .replace(/ticketSetup\./g, "")
                            .replace(/checkout\./g, "")
                            .replace(/location\./g, "Location ");

                        errorMessage = `${fieldName}: ${errorMessage}`;
                    }
                } else if (response.data?.message) {
                    errorMessage = response.data.message;
                }

                console.error("Final error message:", errorMessage);
                toast.error(errorMessage);
                return false;
            }
        } catch (error) {
            console.error("Save draft error:", error);
            toast.error("Failed to save. Please check your connection.");
            return false;
        }
    };

    const handleSaveDraft = async () => {
        try {
            setIsLoading(true);
            setError("");

            const saveSuccess = await saveDraft();
            if (saveSuccess) {
                toast.success("Draft saved successfully!");
            }
        } catch (error) {
            console.error("Save draft error:", error);
            toast.error("Failed to save draft. Please check your connection.");
        } finally {
            setIsLoading(false);
        }
    };

    const publishTicket = async () => {
        try {
            setIsLoading(true);
            setError("");

            // Save draft first
            const saveSuccess = await saveDraft();
            if (!saveSuccess) return;

            // Then publish
            let publishData;
            try {
                publishData = await ticketApi.publishTicket(formData.eventId);
            } catch (err) {
                console.error("Publish API error:", err);
                toast.error(err.message || "Failed to publish ticket. Please try again.");
                return;
            }

            toast.success("Ticket published successfully!");
            setPublishedEvent(publishData.publishedTicket);
            // Re-publish merged any parked edit into the live version — no unpublished changes remain.
            setHasUnpublishedChanges(false);
            setUnpublishedChanges([]);

            // Fetch QR code for the published event
            try {
                const qrCodeData = await ticketApi.getQRCode(formData.eventId);
                setQRCodeData(qrCodeData);
                setShowQRCode(true);
            } catch (qrError) {
                console.error("QR code fetch error:", qrError);
                // Don't fail the entire publish flow if QR generation fails
                setTimeout(() => {
                    navigate("/ticketing/manage");
                }, 3000);
            }
        } catch (error) {
            console.error("Publish error:", error);
            toast.error("Failed to publish ticket. Please check your connection.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDiscardEdit = async () => {
        if (!formData.eventId) return;
        setIsDiscarding(true);
        try {
            await ticketApi.discardEdit(formData.eventId);
            toast.success("Changes discarded");
            setHasUnpublishedChanges(false);
            setUnpublishedChanges([]);
            setShowDiscardConfirm(false);
            // The edit is dropped and the live version is intact — return to Manage Tickets
            // (the card pill reverts to plain "Live" on reload).
            navigate("/ticketing/manage");
        } catch (error) {
            console.error("Discard edit error:", error);
            toast.error(error.message || "Failed to discard changes. Please try again.");
        } finally {
            setIsDiscarding(false);
        }
    };

    const handleConfirmOwnership = async () => {
        try {
            setIsLoading(true);
            setError("");

            // Build payload with EXACT structure backend expects.
            // Convert the entered wall-clock times to UTC in the EVENT's own timezone (the zone
            // shown in the wizard's timezone field), falling back to the org tz then browser-local.
            const eventDetails = { ...formData.eventDetails };
            const saveTz = eventDetails.timezone || orgTimezone;
            eventDetails.startDateTime = localInputToUTCISO(eventDetails.startDateTime, saveTz);
            eventDetails.endDateTime = localInputToUTCISO(eventDetails.endDateTime, saveTz);

            delete eventDetails.eventImageUrl;

            // Remove icsData if it's null (backend expects object or omitted, not null)
            if (eventDetails.icsData === null) {
                delete eventDetails.icsData;
            }

            // Clean up ticket setup - remove empty salesPeriod dates; convert non-empty
            // ones to UTC in the event's tz (mirrors saveDraft / publish).
            const ticketSetup = { ...formData.ticketSetup };
            if (ticketSetup.salesPeriod) {
                ticketSetup.salesPeriod = { ...ticketSetup.salesPeriod };
                if (!ticketSetup.salesPeriod.startDate) {
                    delete ticketSetup.salesPeriod.startDate;
                } else {
                    ticketSetup.salesPeriod.startDate = localInputToUTCISO(ticketSetup.salesPeriod.startDate, saveTz);
                }
                if (!ticketSetup.salesPeriod.endDate) {
                    delete ticketSetup.salesPeriod.endDate;
                } else {
                    ticketSetup.salesPeriod.endDate = localInputToUTCISO(ticketSetup.salesPeriod.endDate, saveTz);
                }
                if (Object.keys(ticketSetup.salesPeriod).length === 0) {
                    delete ticketSetup.salesPeriod;
                }
            }

            // Set default refund policy if empty
            if (!ticketSetup.refundPolicy || ticketSetup.refundPolicy.trim() === "") {
                ticketSetup.refundPolicy =
                    "Refunds are allowed up to 24 hours before the event. Processing fees are non-refundable. Contact organizer for special circumstances.";
            }

            // Persist ticket-type prices as integer cents (the form holds dollars).
            ticketSetup.ticketTypes = ticketTypesToCents(ticketSetup.ticketTypes);

            const payload = {
                eventDetails,
                ticketSetup,
                checkout: {
                    // Send ONLY the schema-allowed checkout fields. Spreading
                    // ...formData.checkout leaks server-managed fields (e.g. `published`)
                    // back to POST /draft, which the draft schema rejects — this broke
                    // editing an already-published event.
                    paymentMethods: ['stripe'], // MVP: Stripe only, preset for backend validation
                    termsAndConditions: formData.checkout?.termsAndConditions ?? '',
                },
                currentStep: formData.currentStep,
                claimOwnership: true, // REQUIRED - signals backend to skip duplicate check
                existingEventId: ownershipPrompt.existingEventId, // REQUIRED - event to link to
            };

            console.log("Claiming ownership for existing event:", ownershipPrompt.existingEventId);
            const response = await ticketApi.saveDraft(payload);

            if (response.ok) {
                // Update form with returned event ID
                if (response.data.ticketDraft.id) {
                    dispatch({
                        type: "SET_EVENT_ID",
                        eventId: response.data.ticketDraft.id,
                    });
                    // Update URL to include draft ID
                    window.history.replaceState({}, "", `?draft=${response.data.ticketDraft.id}`);
                }

                // Close modal and reset states
                setShowOwnershipModal(false);
                setOwnershipPrompt(null);
                setOwnershipAcknowledged(false);
                toast.success("Event ownership claimed successfully!");
            } else {
                // Handle error response
                const errorMessage = response.data?.message || "Failed to claim event ownership. Please try again.";
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error("Ownership confirmation error:", error);
            setError("Failed to claim event ownership. Please check your connection.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelOwnership = () => {
        console.log("🚫 User canceled ownership claim - clearing state");

        // Close the modal
        setShowOwnershipModal(false);
        setOwnershipPrompt(null);
        setOwnershipAcknowledged(false);

        // Clear the form data to start fresh
        dispatch({ type: "RESET_FORM" });

        // Reset to step 1
        dispatch({ type: "SET_STEP", step: 1 });

        // Reload the page to ensure ALL state is cleared including file inputs
        // This is the most reliable way to clear file input state which can't be cleared programmatically
        window.location.reload();
    };

    // Handle ownership conflict from ICS import (triggered immediately after upload)
    const handleOwnershipConflict = (conflictData) => {
        console.log("🔒 Ownership conflict received from ICS import:", conflictData);

        const { existingEvent, ownershipInfo, newEvent } = conflictData;

        // Check backend action guidance: EDIT_EXISTING, CLAIM_OWNERSHIP, or BLOCK
        const action = ownershipInfo.action;

        if (action === "EDIT_EXISTING") {
            // YOU already own this event's tickets - offer to edit
            const ticketConfigId = ownershipInfo.ticketConfigId;
            const startDate = existingEvent.eventStartDateTime
                ? new Date(existingEvent.eventStartDateTime).toLocaleDateString('en-AU', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : 'unknown date';

            // Store event data for modal
            setExistingEventData({
                title: existingEvent.title,
                startDate,
                ticketConfigId
            });
            setShowEditTicketsModal(true);
            return; // Exit early - don't show ownership modal
        }

        if (action === "BLOCK") {
            // Someone else owns this - show error message
            const orgName = ownershipInfo.ticketOrganisationName || "another organiser";
            alert(
                `This event already has tickets created by "${orgName}".\n\n` +
                `Only one organiser can create tickets for an event.\n\n` +
                `Please contact the event organiser if you believe this is an error.`
            );
            return; // Exit early - don't show ownership modal
        }

        // action === "CLAIM_OWNERSHIP" or no action (legacy) - continue with modal

        // CRITICAL: Populate form with newEvent data BEFORE showing modal
        // This ensures form fields are filled when user returns after claiming ownership
        if (newEvent) {
            const eventData = {
                title: newEvent.title,
                description: newEvent.description || "",
                // Convert UTC ISO instants to the org's local wall-clock for the form
                // (org tz when known, else browser-local; a raw slice keeps UTC digits).
                startDateTime: isoToLocalDateTimeInput(newEvent.eventStartDateTime, orgTimezone),
                endDateTime: isoToLocalDateTimeInput(newEvent.eventEndDateTime, orgTimezone),
                location: {
                    description: newEvent.eventLocation?.description || "",
                    latitude: newEvent.eventLocation?.latitude || null,
                    longitude: newEvent.eventLocation?.longitude || null,
                },
                importSource: "ics",
                icsData: newEvent.icsData || {
                    originalUrl: newEvent.facebookUrl,
                    uid: newEvent.icalUID,
                    filename: "imported-event.ics",
                },
                eventImageKey: existingEvent.eventImageKey || null, // Load existing event image key for Base64 API
            };

            dispatch({
                type: "UPDATE_SECTION",
                section: "eventDetails",
                data: eventData,
            });

        }

        // Create ownership prompt data structure expected by modal
        const promptData = {
            existingEventId: existingEvent.id,
            eventDetails: {
                title: existingEvent.title,
                startDateTime: existingEvent.eventStartDateTime,
                location: existingEvent.eventLocation?.description,
                createdAt: existingEvent.createdAt,
            },
            ownershipInfo: ownershipInfo,
            promptMessage: generateOwnershipMessage(ownershipInfo),
        };

        setOwnershipPrompt(promptData);
        setShowOwnershipModal(true);
    };

    // Handle edit existing tickets confirmation
    const handleConfirmEditTickets = () => {
        if (existingEventData?.ticketConfigId) {
            navigate(`/create-tickets?draft=${existingEventData.ticketConfigId}&mode=edit`);
        }
        setShowEditTicketsModal(false);
        setExistingEventData(null);
    };

    // Generate appropriate ownership message based on ownership info
    const generateOwnershipMessage = (ownershipInfo) => {
        if (ownershipInfo.hasTickets) {
            if (ownershipInfo.ticketOrganisationName) {
                return `This event already has tickets created by "${ownershipInfo.ticketOrganisationName}". Only one organiser can create tickets for an event. Please contact the event organiser if you believe this is an error.`;
            } else {
                return `This event already has tickets created by another organiser. Only one organiser can create tickets for an event.`;
            }
        } else if (ownershipInfo.uploadedByAdminUser) {
            return `This event was uploaded to the Dance Localé gallery by an admin. By proceeding, you confirm that you are the event organizer and have the right to create tickets for this event.`;
        } else {
            return `This event already exists. By proceeding, you confirm that you are the event organizer and have the right to create tickets for this event.`;
        }
    };

    // Render current step component
    const renderCurrentStep = () => {
        const currentStepConfig = WIZARD_STEPS.find(step => step.id === formData.currentStep);
        if (!currentStepConfig) return null;

        const stepProps = {
            formData,
            updateFormSection,
            isLoading,
            error,
            setError,
            orgCurrency,
            orgTimezone,
        };

        switch (currentStepConfig.component) {
            case "EventDetailsForm":
                // Pass ownership conflict handler, user, and editMode for ICS upload ownership checking
                return <EventDetailsForm {...stepProps} onOwnershipConflict={handleOwnershipConflict} user={user} editMode={editMode} />;
            case "TicketSetupForm":
                return <TicketSetupForm {...stepProps} editMode={editMode} soldCounts={soldCounts} />;
            case "CheckoutForm":
                return <CheckoutForm {...stepProps} onPublish={publishTicket} onSaveDraft={handleSaveDraft} editMode={editMode} payoutsReady={payoutsReady} />;
            default:
                return null;
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className={`dashboard-container org-console ticket-wizard-page${isDevMode ? ' dev-mode' : ''}`}>
            {/* TODO: Add SEO meta tags when react-helmet-async supports React 19
                Title: "Create Tickets - Dance Localé"
                OG Title: "Create Tickets - Dance Localé" */}

            {/* Header */}
            <AppHeader />

            {/* Main Content */}
            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    {/* Header Section */}
                    <div className="sub-portal-header-card" style={{ position: 'relative' }}>
                        {/* Dev mode badge - top right corner of header card */}
                        {isDevMode && (
                            <div style={{
                                position: 'absolute',
                                top: '10px',
                                right: '12px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 10px',
                                background: 'rgba(255, 152, 0, 0.1)',
                                border: '1px solid rgba(255, 152, 0, 0.3)',
                                borderRadius: '12px',
                                fontSize: '0.7rem',
                                color: '#ff9800',
                                fontWeight: '500',
                            }}>
                                <span>🧪</span>
                                <span>DEV DB</span>
                            </div>
                        )}

                        <div className="sub-portal-header-title-row">
                            {/* Back Button - direct child so CSS positions it absolutely at left */}
                            <button
                                onClick={() => navigate('/ticketing')}
                                className="back-nav-btn"
                            >
                                ← Ticketing Portal
                            </button>

                            <h1 className="sub-portal-header-title">
                                {editMode ? 'Edit Published Event' : 'Create Tickets'}
                            </h1>
                        </div>

                        {/* Edit-mode info badge + Discard action on one row. The badge is an
                            informational status (soft, not a bright warning). Discard appears to
                            its right only when there are parked (unpublished) edits — the
                            "Live - unpublished edits" status pill lives on the Manage page card. */}
                        {editMode && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '12px',
                                marginTop: '8px',
                                flexWrap: 'wrap',
                            }}>
                                <div className="ticket-wizard-edit-hint">
                                    {hasUnpublishedChanges && unpublishedChanges.length > 0 ? (
                                        <>Unpublished changes: <strong>{unpublishedChanges.map(c => c.label).join(", ")}</strong>. These remain in draft until you publish them in Step 3.</>
                                    ) : (
                                        <>Changes remain in draft until you publish them in Step 3.</>
                                    )}
                                </div>
                                {hasUnpublishedChanges && (
                                    <button
                                        className="btn-danger"
                                        onClick={() => setShowDiscardConfirm(true)}
                                        disabled={isDiscarding}
                                        title="Discard your unpublished edits and revert to the live version"
                                    >
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Icon path={mdiArrowULeftTop} size={0.75} />
                                            Discard changes
                                        </span>
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Step Progress Indicator */}
                        <WizardSteps
                            steps={WIZARD_STEPS}
                            currentStep={formData.currentStep}
                            onStepClick={goToStep}
                            completedSteps={formData.currentStep - 1}
                        />
                    </div>

                    <div className="ticket-wizard-form-card">

                    {/* Top step navigation — mirrors the buttons at the bottom so the user can
                        move between steps without scrolling to the foot of the form. */}
                    <div className="ticket-wizard-navigation ticket-wizard-navigation-top">
                        <div className="navigation-primary">
                            {formData.currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={goToPreviousStep}
                                    className="back-nav-btn"
                                    disabled={isLoading}>
                                    ← Back
                                </button>
                            )}

                            {formData.currentStep < WIZARD_STEPS.length && (
                                <button type="button" onClick={goToNextStep} className="btn-purple" style={{ marginLeft: 'auto' }} disabled={isLoading}>
                                    {isLoading ? "Saving..." : "Next →"}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Validation errors only - API/success messages use toasts */}
                    {error && (
                        <div className="status-message error" style={{ marginBottom: '16px' }}>
                            <span className="status-icon">⚠</span>
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Form Content */}
                    <div className="ticket-wizard-form" style={{ position: 'relative' }}>
                        {renderCurrentStep()}

                        {/* Loading overlay - shows immediately when isLoading=true, hides immediately when false */}
                        {isLoading && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(2px)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1000,
                                borderRadius: '18px'
                            }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div className="web-fallback-spinner" style={{
                                        width: '50px',
                                        height: '50px',
                                        border: '4px solid #f3f3f3',
                                        borderTop: '4px solid #451f78',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        margin: '0 auto 16px'
                                    }} />
                                    <p style={{ color: '#451f78', fontWeight: 600 }}>Loading draft...</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Navigation Buttons */}
                    <div className="ticket-wizard-navigation">
                        <div className="navigation-primary">
                            {formData.currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={goToPreviousStep}
                                    className="back-nav-btn"
                                    disabled={isLoading}>
                                    ← Back
                                </button>
                            )}

                            {formData.currentStep < WIZARD_STEPS.length && (
                                <button type="button" onClick={goToNextStep} className="btn-purple" style={{ marginLeft: 'auto' }} disabled={isLoading}>
                                    {isLoading ? "Saving..." : "Next →"}
                                </button>
                            )}
                        </div>
                    </div>

                    </div> {/* end ticket-wizard-form-card */}

                </div>
            </div>

            {/* QR Code Success Modal */}
            <BaseModal
                isOpen={showQRCode && !!qrCodeData}
                onClose={() => { setShowQRCode(false); navigate("/ticketing/manage"); }}
                title="Event Published!"
                icon={mdiCheckCircle}
                iconColor="#0e9e0e"
                maxWidth={560}
                footer={
                    <>
                        <button
                            className="base-modal-cancel-btn"
                            onClick={() => { setShowQRCode(false); navigate("/ticketing/manage"); }}
                        >
                            Go to Ticketing
                        </button>
                        <button
                            className="base-modal-save-btn ticket-modal-btn-turquoise"
                            onClick={() => {
                                const link = document.createElement('a');
                                link.download = `${qrCodeData?.eventTitle || 'event'}-qr-code.png`;
                                link.href = qrCodeData?.qrCode;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}
                        >
                            <Icon path={mdiDownload} size={0.8} />
                            Download QR
                        </button>
                        <button
                            className="base-modal-save-btn"
                            onClick={() => {
                                const printWindow = window.open("", "_blank");
                                printWindow.document.write(`
                                    <html>
                                    <head>
                                        <title>Event Check-in QR Code - ${qrCodeData?.eventTitle}</title>
                                        <style>
                                            @page { size: A4 portrait; margin: 20mm; }
                                            body {
                                                margin: 0;
                                                font-family: Arial, sans-serif;
                                                text-align: center;
                                            }
                                            .container {
                                                display: flex;
                                                flex-direction: column;
                                                align-items: center;
                                                padding-top: 32px;
                                            }
                                            h1 {
                                                font-size: 30px;
                                                font-weight: 700;
                                                margin: 0 0 6px;
                                                color: #451f78; /* --dl-color-purple-default — CSS vars unavailable in popup window */
                                            }
                                            .label {
                                                font-size: 13px;
                                                color: #888;
                                                margin: 0 0 28px;
                                                text-transform: uppercase;
                                                letter-spacing: 1px;
                                            }
                                            img {
                                                width: 320px;
                                                height: 320px;
                                            }
                                            .instruction {
                                                margin: 0 0 20px;
                                                font-size: 16px;
                                                color: #555;
                                            }
                                        </style>
                                    </head>
                                    <body>
                                        <div class="container">
                                            <h1>${qrCodeData?.eventTitle}</h1>
                                            <p class="label">Event Check-in QR Code</p>
                                            <p class="instruction">Scan with Dance Localé mobile app for check-in</p>
                                            <img src="${qrCodeData?.qrCode}" alt="QR Code" />
                                        </div>
                                    </body>
                                    </html>
                                `);
                                printWindow.document.close();
                                printWindow.focus();
                                setTimeout(() => {
                                    printWindow.print();
                                    printWindow.close();
                                }, 250);
                            }}
                        >
                            <Icon path={mdiPrinterOutline} size={0.8} />
                            Print QR
                        </button>
                    </>
                }
            >
                <p style={{ margin: '0 0 12px', color: 'var(--dl-color-medium)' }}>
                    Your event "{qrCodeData?.eventTitle}" is now live with tickets available for purchase.
                </p>
                <div className="qr-code-section">
                    <h3 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px' }}>Event Check-in QR Code</h3>
                    <p style={{ fontSize: '0.85rem', color: 'var(--dl-color-medium)', marginBottom: '12px' }}>
                        Display this QR code at your event entrance for attendee check-in:
                    </p>
                    <div style={{ textAlign: 'center', margin: '0 0 16px' }}>
                        <img src={qrCodeData?.qrCode} alt="Event Check-in QR Code" className="qr-code-image" />
                    </div>
                    <div className="qr-code-instructions">
                        <h4 style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px' }}>How to use:</h4>
                        <ol style={{ paddingLeft: '18px', fontSize: '0.85rem', color: 'var(--dl-color-medium)', lineHeight: '1.7' }}>
                            <li>Print or display this QR code at your event entrance</li>
                            <li>Ticket holders scan the code with the Dance Localé mobile app</li>
                            <li>App confirms their ticket and marks them as checked in</li>
                        </ol>
                    </div>
                </div>
            </BaseModal>

            {/* Ownership Acknowledgment Modal */}
            <BaseModal
                isOpen={showOwnershipModal && !!ownershipPrompt}
                onClose={handleCancelOwnership}
                title="Event Already Exists"
                icon={mdiAlertCircleOutline}
                iconColor="var(--dl-color-needs-action)"
                maxWidth={520}
                footer={
                    <>
                        <button className="base-modal-cancel-btn" onClick={handleCancelOwnership} disabled={isLoading}>
                            Cancel
                        </button>
                        <button
                            className="base-modal-save-btn"
                            onClick={handleConfirmOwnership}
                            disabled={!ownershipAcknowledged || isLoading}
                        >
                            {isLoading ? "Claiming..." : "Claim Ownership"}
                        </button>
                    </>
                }
            >
                <p style={{ margin: '0 0 16px', fontWeight: '500' }}>
                    Please confirm you are the event organizer
                </p>
                <div className="ownership-event-details">
                    <h3 style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px' }}>Existing Event Details:</h3>
                    <div className="ownership-detail-row">
                        <strong>Title:</strong>
                        <span>{ownershipPrompt?.eventDetails?.title}</span>
                    </div>
                    <div className="ownership-detail-row">
                        <strong>Date:</strong>
                        <span>
                            {ownershipPrompt?.eventDetails?.startDateTime
                                ? new Date(ownershipPrompt.eventDetails.startDateTime).toLocaleString('en-AU', {
                                    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                })
                                : 'Unknown date'}
                        </span>
                    </div>
                    <div className="ownership-detail-row">
                        <strong>Location:</strong>
                        <span>{ownershipPrompt?.eventDetails?.location || 'Unknown location'}</span>
                    </div>
                </div>
                <div className="ownership-warning" style={{ margin: '12px 0' }}>
                    <p>{ownershipPrompt?.promptMessage}</p>
                </div>
                <div className="ownership-checkbox">
                    <CustomFormInput
                        type="checkbox"
                        variant="green"
                        layout="standard"
                        label="I confirm that I am the event organizer and have the right to create tickets for this event"
                        checked={ownershipAcknowledged}
                        onChange={(e) => setOwnershipAcknowledged(e.target.checked)}
                    />
                </div>
            </BaseModal>

            {/* Edit Existing Tickets Confirmation Modal */}
            <ConfirmationModal
                isOpen={showEditTicketsModal}
                title="Edit Existing Tickets?"
                message={existingEventData ? `You already created tickets for "${existingEventData.title}" (${existingEventData.startDate}).\n\nWould you like to edit your existing tickets instead?` : ''}
                confirmText="Edit Tickets"
                cancelText="Start Over"
                onConfirm={handleConfirmEditTickets}
                onCancel={() => {
                    setShowEditTicketsModal(false);
                    setExistingEventData(null);
                }}
                variant="primary"
            />

            {/* Discard unpublished changes confirmation */}
            <ConfirmationModal
                isOpen={showDiscardConfirm}
                title="Discard unpublished changes?"
                message={"Your in-progress edits will be removed and the event will go back to its currently published version.\n\nThe live event stays online the whole time."}
                confirmText="Discard changes"
                cancelText="Keep editing"
                onConfirm={handleDiscardEdit}
                onCancel={() => setShowDiscardConfirm(false)}
                variant="warning"
            />
        </div>
    );
};

export default TicketWizard;
